import { SpatialLayer } from '@innovyze/lib-am-common';
import {
	CachedField,
	Component,
	ComponentField,
	ComponentType,
	SimulationData,
	TaskData,
} from '@Types';

/** The Score Setup picker options. Each list is undefined until it has
 *  loaded, so a list that is still loading (or failed) never makes a
 *  component look disconnected. */
export interface ComponentSourceLists {
	lofFields?: ComponentField[];
	lofInspectionFields?: ComponentField[];
	/** The config has an inspection standard, so LoF condition categories
	 *  pick from the inspection fields (the page only passes inspectionType
	 *  to the Field picker when it does). */
	hasInspectionStandard?: boolean;
	cofFields?: ComponentField[];
	spatialLayers?: SpatialLayer[];
	spatialAttributes?: CachedField[];
	taskData?: TaskData[];
	simulationData?: SimulationData[];
}

/** Matches the availability `kind`s the template apply sends, which the
 *  "Not in your data" tooltip already labels. */
export type MissingSourceKind =
	| 'attribute'
	| 'observation'
	| 'spatialProximity'
	| 'spatialAttribute'
	| 'task'
	| 'simulation';

// Uncached fields are offered disabled, so they can't be picked and count as
// missing (as the backend's availability check also treats them).
const hasCachedField = (fields: ComponentField[], field?: string) =>
	fields.some(f => f.cachedField === field && f.isCached);

const attributeMissingKind = (
	component: Component,
	forLoF: boolean,
	isCondition: boolean,
	lists: ComponentSourceLists,
): MissingSourceKind | undefined => {
	// Same list the Field picker offers: inspection fields for LoF condition
	// categories (RenderedCategories' forInspection) when the config has an
	// inspection standard (RiskManagement.page's inspectionType).
	const isInspection = forLoF && isCondition && !!lists.hasInspectionStandard;
	let fields = lists.cofFields;
	if (forLoF) {
		fields = isInspection ? lists.lofInspectionFields : lists.lofFields;
	}
	if (!fields || hasCachedField(fields, component.field)) return undefined;
	return isInspection ? 'observation' : 'attribute';
};

/**
 * What kind of source a saved component is missing, or undefined when it is
 * connected (or can't be told yet). "Connected" means the saved source is
 * one of the options its picker would offer right now, so a template's
 * default layer/field name that matches nothing in the tenant is missing.
 * Types without a reconnect picker (multi-param, custom table, failure
 * definition, deterioration model, PACP observation codes) are never missing.
 */
export const getMissingSourceKind = (
	component: Component,
	forLoF: boolean,
	isCondition: boolean,
	lists: ComponentSourceLists,
): MissingSourceKind | undefined => {
	switch (component.type) {
		case ComponentType.Attribute:
			return attributeMissingKind(component, forLoF, isCondition, lists);
		case ComponentType.SpatialProximity:
			if (
				!lists.spatialLayers ||
				lists.spatialLayers.some(l => l.LAYER_NAME === component.layer)
			)
				return undefined;
			return 'spatialProximity';
		case ComponentType.SpatialAttribute:
			if (
				!lists.spatialAttributes ||
				lists.spatialAttributes.some(
					a =>
						a.layerName === component.layer &&
						a.cachedField === component.field,
				)
			)
				return undefined;
			return 'spatialAttribute';
		case ComponentType.Task: {
			if (!lists.taskData) return undefined;
			const upload = lists.taskData.find(
				t => t.uploadId === component.uploadId,
			);
			return upload?.cachedFields.some(
				f => f.cachedField === component.field,
			)
				? undefined
				: 'task';
		}
		case ComponentType.Simulation: {
			if (!lists.simulationData) return undefined;
			const run = lists.simulationData.find(
				s => s.uploadId === component.simulationUploadId,
			);
			// The simulation Field picker saves display names; accept the
			// cached field name too in case an older save stored that.
			return run?.cachedFields.some(
				f =>
					f.userFriendlyName === component.field ||
					f.cachedField === component.field,
			)
				? undefined
				: 'simulation';
		}
		default:
			return undefined;
	}
};
